import React from 'react';
import { useTheme } from './ThemeProvider';

// ── Button ────────────────────────────────────────────────

type ButtonVariant = 'primary' | 'secondary' | 'ghost';
type ButtonSize = 'sm' | 'md' | 'lg';

const BUTTON_SIZE: Record<ButtonSize, { padding: string; fontSize: string; height: string }> = {
  sm: { padding: '4px 12px',  fontSize: '13px', height: '30px' },
  md: { padding: '8px 20px',  fontSize: '15px', height: '40px' },
  lg: { padding: '12px 28px', fontSize: '16px', height: '48px' },
};

export function Button({
  variant = 'primary',
  size = 'md',
  children,
  onClick,
  disabled,
  type = 'button',
  style,
  className,
}: {
  variant?: ButtonVariant;
  size?: ButtonSize;
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  type?: 'button' | 'submit' | 'reset';
  style?: React.CSSProperties;
  className?: string;
}) {
  const t = useTheme();
  const sz = BUTTON_SIZE[size];
  const base: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    padding: sz.padding,
    height: sz.height,
    borderRadius: t.radius.md,
    border: variant === 'secondary' ? `1.5px solid ${t.color.primary}` : 'none',
    background:
      variant === 'primary'   ? t.color.primary :
      variant === 'secondary' ? t.color.surface :
      'transparent',
    color: variant === 'primary' ? t.color.surface : t.color.primary,
    fontFamily: t.font.family,
    fontSize: sz.fontSize,
    fontWeight: t.font.weight.semibold,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.45 : 1,
    transition: 'opacity 0.15s, transform 0.1s',
    textDecoration: 'none',
    boxSizing: 'border-box',
    ...style,
  };
  return (
    <button style={base} onClick={onClick} disabled={disabled} type={type} className={className}>
      {children}
    </button>
  );
}

// ── Card ──────────────────────────────────────────────────

type CardVariant = 'default' | 'primary' | 'secondary' | 'accent';

export function Card({
  variant = 'default',
  onClick,
  children,
  style,
  className,
  role,
}: {
  variant?: CardVariant;
  onClick?: () => void;
  children: React.ReactNode;
  style?: React.CSSProperties;
  className?: string;
  role?: string;
}) {
  const t = useTheme();

  const variantStyles: React.CSSProperties =
    variant === 'primary' ? {
      background: `linear-gradient(135deg, ${t.color.primary} 0%, ${t.color.secondary} 100%)`,
      color: '#fff',
    } :
    variant === 'secondary' ? {
      background: t.color.secondary,
      color: '#fff',
    } :
    variant === 'accent' ? {
      background: t.color.accent,
      color: '#fff',
    } :
    {
      background: t.color.surface,
      border: `1px solid ${t.color.border}`,
      color: t.color.text,
    };

  const base: React.CSSProperties = {
    borderRadius: t.radius.lg,
    padding: `${t.spacing.md} 18px`,
    boxShadow: t.shadow.sm,
    fontFamily: t.font.family,
    display: 'block',
    ...variantStyles,
    ...style,
  };

  return (
    <div style={base} onClick={onClick} className={className} role={role}>
      {children}
    </div>
  );
}

export function CardHeader({
  children,
  style,
}: {
  children: React.ReactNode;
  style?: React.CSSProperties;
}) {
  const t = useTheme();
  return (
    <div style={{
      paddingBottom: t.spacing.sm,
      borderBottom: `1px solid ${t.color.border}`,
      marginBottom: t.spacing.sm,
      fontWeight: t.font.weight.semibold,
      fontSize: t.font.size.base,
      color: t.color.text,
      ...style,
    }}>
      {children}
    </div>
  );
}

export function CardBody({
  children,
  style,
}: {
  children: React.ReactNode;
  style?: React.CSSProperties;
}) {
  return <div style={{ fontSize: '14px', ...style }}>{children}</div>;
}

export function CardFooter({
  children,
  style,
}: {
  children: React.ReactNode;
  style?: React.CSSProperties;
}) {
  const t = useTheme();
  return (
    <div style={{
      paddingTop: t.spacing.sm,
      borderTop: `1px solid ${t.color.border}`,
      marginTop: t.spacing.sm,
      display: 'flex',
      alignItems: 'center',
      gap: t.spacing.sm,
      ...style,
    }}>
      {children}
    </div>
  );
}

// ── Badge ─────────────────────────────────────────────────

type BadgeVariant = 'default' | 'primary' | 'info' | 'success' | 'warning' | 'error';

export function Badge({
  variant = 'default',
  children,
  style,
}: {
  variant?: BadgeVariant;
  children: React.ReactNode;
  style?: React.CSSProperties;
}) {
  const t = useTheme();

  type ColorPair = { bg: string; color: string };
  const variantMap: Record<BadgeVariant, ColorPair> = {
    default: { bg: t.color.grey100,  color: t.color.grey600 },
    primary: { bg: `${t.color.primary}1A`, color: t.color.primary },
    info:    { bg: `${t.color.primary}1A`, color: t.color.primary },
    success: { bg: '#DCFCE7', color: t.color.success },
    warning: { bg: '#FEF3C7', color: t.color.warning },
    error:   { bg: '#FEE2E2', color: t.color.error },
  };

  const { bg, color } = variantMap[variant];

  return (
    <span style={{
      display: 'inline-block',
      background: bg,
      color,
      fontSize: t.font.size.xs,
      fontWeight: t.font.weight.semibold,
      lineHeight: '1',
      padding: '3px 8px',
      borderRadius: t.radius.full,
      fontFamily: t.font.family,
      letterSpacing: '0.2px',
      ...style,
    }}>
      {children}
    </span>
  );
}

// ── Screen ────────────────────────────────────────────────

export function Screen({
  title,
  children,
}: {
  title?: string;
  children: React.ReactNode;
}) {
  const t = useTheme();
  return (
    <div style={{
      minHeight: '100vh',
      background: t.color.surfaceAlt,
      fontFamily: t.font.family,
      color: t.color.text,
      padding: t.spacing.lg,
      paddingTop: `calc(${t.spacing.xl} + env(safe-area-inset-top))`,
    }}>
      {title && (
        <h1 style={{
          fontSize: t.font.size.xl,
          fontWeight: t.font.weight.bold,
          margin: `0 0 ${t.spacing.lg} 0`,
          color: t.color.text,
        }}>
          {title}
        </h1>
      )}
      {children}
    </div>
  );
}
